import type { FC, ReactNode } from 'react'
import type { LayoutProps } from '@/types'

import { useEffect } from 'react'
import { useRouter } from 'next/router'
import Lenis from 'lenis'

import { gsap, ScrollTrigger } from '@/lib/gsap'
import { setLenis } from '@/lib/lenis-store'

/**
 * Drives Lenis from the GSAP ticker so smooth scrolling and ScrollTrigger
 * share a single clock. Without this the two run on separate rAF loops and
 * scrubbed animations visibly lag behind the scroll position.
 */
export const SmoothScroll: FC<LayoutProps> = ({ children }): ReactNode => {
    const router = useRouter()

    useEffect(() => {
        const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches

        if (reduced) {
            return
        }

        const lenis = new Lenis({
            duration: 1.15,
            easing: (t: number) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
            touchMultiplier: 1.8,
            wheelMultiplier: 1
        })

        const update = (time: number) => lenis.raf(time * 1000)

        setLenis(lenis)
        lenis.on('scroll', ScrollTrigger.update)
        gsap.ticker.add(update)
        gsap.ticker.lagSmoothing(0)

        // Anchor links have to go through Lenis or they jump instantly.
        const onAnchorClick = (event: MouseEvent) => {
            const target = event.target as HTMLElement | null
            const anchor = target?.closest<HTMLAnchorElement>('a[href^="#"]')
            const hash = anchor?.getAttribute('href')

            if (!anchor || !hash || hash === '#') {
                return
            }

            const section = document.querySelector(hash)

            if (!section) {
                return
            }

            event.preventDefault()
            lenis.scrollTo(section as HTMLElement, { offset: -12, duration: 1.3 })
        }

        document.addEventListener('click', onAnchorClick)

        return () => {
            document.removeEventListener('click', onAnchorClick)
            gsap.ticker.remove(update)
            lenis.destroy()
            setLenis(null)
        }
    }, [])

    // Every route change needs the scroll reset and the triggers recalculated
    // against the new page height.
    useEffect(() => {
        const onComplete = () => {
            window.scrollTo(0, 0)
            ScrollTrigger.refresh()
        }

        router.events.on('routeChangeComplete', onComplete)

        return () => router.events.off('routeChangeComplete', onComplete)
    }, [router.events])

    return <>{children}</>
}
